import React from 'react';

import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';

import { Alert, Checkbox, Pill, Text } from '@components/common';
import { EmailSVG, InfoCircleIcon, RemoveIconSVG, RoundCheckMarkSVG, WarningSVG } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';

interface Props {
  netEligibleCount: number;
  netIneligibleCount: number;
  unsubscribedCount: number;
  includeIneligible: boolean;
  setIncludeIneligible: (includeIneligible: boolean) => void;
  setCtaEnabled: (ctaEnabled: boolean) => void;
  studyLimits?: StudyLimit[];
  studyLimitMatches?: Record<number, number>;
  previewCandidate?: Candidate;
}

interface RecipientStatusPillProps {
  count: number;
  label: string;
  color: 'green' | 'yellow' | 'red';
  Icon: React.ElementType;
  tooltipContent?: string;
}

const RecipientStatusContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='space-y-3 rounded-md border border-b border-gray-200 p-4'>{children}</div>
);

const RecipientStatus: React.FC<RecipientStatusPillProps> = ({ count, label, color, Icon, tooltipContent }) => (
  <Tooltip isDisabled={!tooltipContent} content={tooltipContent}>
    <div className='flex items-center space-x-2'>
      <Pill className='flex items-center px-3 py-1' color={color}>
        <Icon className='mr-1 h-4 w-3.5' />
        {count}
      </Pill>
      <span className='text-sm font-bold text-gray-700'>{label}</span>
      {tooltipContent && <InfoCircleIcon className='h-4 w-4 text-gray-400' />}
    </div>
  </Tooltip>
);

export const RecipientsPanel: React.FC<React.PropsWithChildren<Props>> = ({
  netEligibleCount,
  netIneligibleCount,
  unsubscribedCount,
  includeIneligible,
  setIncludeIneligible,
  setCtaEnabled,
  previewCandidate
}) => {
  const { t } = useTranslation('RecipientsPanel');
  const totalRecipients = netEligibleCount + netIneligibleCount + unsubscribedCount;
  const selectedRecipients = includeIneligible ? netEligibleCount + netIneligibleCount : netEligibleCount;
  const toBeSentString = () => {
    if (selectedRecipients === 1 && totalRecipients === 1 && !includeIneligible) {
      return previewCandidate?.name || 'a candidate';
    }

    return `${selectedRecipients} of ${totalRecipients} candidates`;
  };

  React.useEffect(() => {
    setCtaEnabled(selectedRecipients > 0);
  }, [selectedRecipients]);

  return (
    <div className='form-group mb-6 space-y-2'>
      {netIneligibleCount > 0 && unsubscribedCount == 0 && netEligibleCount == 0 && (
        <Alert className='mb-4' type='warning' heading={t('ineligible_title')}>
          {t('ineligible_alert')}
        </Alert>
      )}
      {netIneligibleCount == 0 && unsubscribedCount > 0 && netEligibleCount == 0 && (
        <Alert className='mb-4' type='error' heading={t('unsubscribed_title')}>
          {t('unsubscribed_alert')}
        </Alert>
      )}
      {netIneligibleCount > 0 && unsubscribedCount > 0 && netEligibleCount == 0 && (
        <Alert className='mb-4' type='warning' heading={t('unsubscribed_ineligible_title')}>
          {t('unsubscribed_ineligible_alert')}
        </Alert>
      )}

      <span className='h500-bold mb-2'>Review selected candidates</span>

      <div className='space-y-3 rounded-md border border-b border-gray-200 p-5'>
        <div className='flex w-full justify-between px-3'>
          <div className='flex items-center space-x-2'>
            <EmailSVG className='text-gray-700' height='13' width='18' />
            <span className='text-md font-bold text-gray-700'>Total recipients</span>
          </div>
          <span className='text-md font-bold text-gray-700'>{toBeSentString()}</span>
        </div>

        {netEligibleCount > 0 && (
          <RecipientStatusContainer>
            <RecipientStatus
              count={netEligibleCount}
              label='Eligible candidates'
              color='green'
              Icon={RoundCheckMarkSVG}
            />
          </RecipientStatusContainer>
        )}

        {netIneligibleCount > 0 && (
          <RecipientStatusContainer>
            <RecipientStatus
              count={netIneligibleCount}
              label='Ineligible candidates'
              color='yellow'
              Icon={WarningSVG}
              tooltipContent={t('ineligible_tooltip')}
            />
            <div className='ml-10 flex items-center space-x-2'>
              <Checkbox name='exclude_ineligible' onChange={setIncludeIneligible} selected={includeIneligible} />
              <Text>
                Include {netIneligibleCount} {pluralize('candidate', netIneligibleCount)}
              </Text>
            </div>
          </RecipientStatusContainer>
        )}

        {unsubscribedCount > 0 && (
          <RecipientStatusContainer>
            <RecipientStatus
              count={unsubscribedCount}
              label='Unsubscribed candidates'
              color='red'
              Icon={RemoveIconSVG}
              tooltipContent={t('unsubscribed_tooltip')}
            />
          </RecipientStatusContainer>
        )}
      </div>
    </div>
  );
};
